import React, { useState, useEffect } from 'react';
import ProgramOverview from './components/ProgramOverview';
import CohortExplorer from './components/CohortExplorer';
import ACODeepDive from './components/ACODeepDive';
import QualityDashboard from './components/QualityDashboard';
import ProgramChanges from './components/ProgramChanges';
import Glossary from './components/Glossary';

const NAV = [
  { id: 'overview',  label: 'Program Overview',   icon: '📈', section: 'Analysis' },
  { id: 'cohort',    label: 'Cohort Explorer',     icon: '🔍', section: 'Analysis' },
  { id: 'aco',       label: 'ACO Deep Dive',       icon: '🏥', section: 'Analysis' },
  { id: 'quality',   label: 'Quality Dashboard',   icon: '⭐', section: 'Analysis' },
  { id: 'changes',   label: 'Program Changes',     icon: '📋', section: 'Reference' },
  { id: 'glossary',  label: 'Glossary & Terms',    icon: '📖', section: 'Reference' },
];

const PAGE_TITLES = {
  overview: 'Program Overview',
  cohort:   'Cohort Explorer',
  aco:      'ACO Deep Dive',
  quality:  'Quality Dashboard',
  changes:  'Program Changes',
  glossary: 'Glossary & Terms',
};

const DATA_LAST_UPDATED = 'November 2026';
const DATA_SOURCE_RELEASE = 'PY 2024 (reopened results, updated Jul 2026)';

export default function App() {
  const [page, setPage] = useState('overview');
  const [perfData, setPerfData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deepDiveACO, setDeepDiveACO] = useState(null); // used when clicking ACO name to navigate
  const [deepDiveVersion, setDeepDiveVersion] = useState(0); // increment to force re-selection
  const [pinnedACO, setPinnedACO]   = useState(null); // global ACO context shown across all pages
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    // Load the main performance dataset once at app level
    fetch(`${process.env.PUBLIC_URL}/data/aco_performance.json`)
      .then(r => r.json())
      .then(data => {
        setPerfData(data);
        setLoading(false);
      })
      .catch(err => {
        console.error('Failed to load performance data:', err);
        setLoading(false);
      });
  }, []);

  // Navigate to ACO Deep Dive with a pre-selected ACO
  const navigateToACO = (aco) => {
    setDeepDiveACO(aco);
    setDeepDiveVersion(v => v + 1);
    setPinnedACO(aco);  // also pin it for cross-page context
    setPage('aco');
  };

  // Pin/unpin an ACO for cross-page context without navigating
  const pinACO = (aco) => setPinnedACO(aco);
  const clearPinnedACO = () => setPinnedACO(null);

  const sections = [...new Set(NAV.map(n => n.section))];

  return (
    <div className="app-layout">
      {/* ── Sidebar ── */}
      <aside className="sidebar">
        <div className="sidebar-brand">
          <div className="sidebar-brand-name" style={{ letterSpacing: '0.08em' }}>TRYNYTY</div>
          <div style={{ fontSize: '0.78rem', fontWeight: 400, marginTop: 1 }}>
            <span style={{ color: 'rgba(255,255,255,0.5)', letterSpacing: '0.02em' }}>health</span>
            <span style={{ color: '#0070C0', letterSpacing: '0.02em' }}>:enablement</span>
          </div>
          <div className="sidebar-brand-sub" style={{ marginTop: 6 }}>MSSP ACO Explorer</div>
        </div>

        <nav className="sidebar-nav">
          {sections.map(section => (
            <div key={section}>
              <div className="nav-section-label">{section}</div>
              {NAV.filter(n => n.section === section).map(item => (
                <button
                  key={item.id}
                  className={`nav-item ${page === item.id ? 'active' : ''}`}
                  onClick={() => { setPage(item.id); setDrawerOpen(false); }}
                >
                  <span className="nav-icon">{item.icon}</span>
                  {item.label}
                </button>
              ))}
            </div>
          ))}
        </nav>

        <div className="sidebar-footer">
          Data: CMS MSSP Public Use Files<br />
          PY 2013 – 2024 &nbsp;|&nbsp; {loading ? '…' : '5,476 ACO-years'}
          <div style={{ marginTop: 6, fontSize: 10, color: 'rgba(255,255,255,0.35)', lineHeight: 1.5 }}>
            Last updated: {DATA_LAST_UPDATED}<br/>
            Source: {DATA_SOURCE_RELEASE}
          </div>
        </div>
      </aside>

      {/* ── Mobile drawer overlay ── */}
      <div className={`mobile-overlay ${drawerOpen ? 'open' : ''}`} onClick={() => setDrawerOpen(false)} />

      {/* ── Mobile drawer ── */}
      <div className={`mobile-drawer ${drawerOpen ? 'open' : ''}`}>
        <div className="sidebar-brand" style={{ padding: '16px 16px 12px' }}>
          <div className="sidebar-brand-name" style={{ letterSpacing: '0.08em' }}>TRYNYTY</div>
          <div style={{ fontSize: '0.78rem', fontWeight: 400, marginTop: 1 }}>
            <span style={{ color: 'rgba(255,255,255,0.5)' }}>health</span>
            <span style={{ color: '#0070C0' }}>:enablement</span>
          </div>
          <div className="sidebar-brand-sub" style={{ marginTop: 6 }}>MSSP ACO Explorer</div>
        </div>
        <nav className="sidebar-nav">
          {sections.map(section => (
            <div key={section}>
              <div className="nav-section-label">{section}</div>
              {NAV.filter(n => n.section === section).map(item => (
                <button key={item.id}
                  className={`nav-item ${page === item.id ? 'active' : ''}`}
                  onClick={() => { setPage(item.id); setDrawerOpen(false); }}
                >
                  <span className="nav-icon">{item.icon}</span>
                  {item.label}
                </button>
              ))}
            </div>
          ))}
        </nav>
      </div>

      {/* ── Main ── */}
      <div className="main-area">
        <div className="topbar">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button
              className="hamburger-btn"
              onClick={() => setDrawerOpen(o => !o)}
              style={{ display: 'none', flexDirection: 'column', gap: 4, background: 'none',
                border: 'none', cursor: 'pointer', padding: '4px 6px' }}
            >
              <span style={{ display: 'block', width: 20, height: 2, background: 'var(--gray-600)', borderRadius: 2 }} />
              <span style={{ display: 'block', width: 20, height: 2, background: 'var(--gray-600)', borderRadius: 2 }} />
              <span style={{ display: 'block', width: 20, height: 2, background: 'var(--gray-600)', borderRadius: 2 }} />
            </button>
            <div className="topbar-title">{PAGE_TITLES[page]}</div>
          </div>
          <span className="topbar-badge">PY 2013 – 2024</span>
        </div>

        {/* ── Pinned ACO banner ── */}
        {pinnedACO && (
          <div className="pinned-aco-banner" style={{ borderLeft: "4px solid #FF6B35" }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 16, color: "#FF6B35" }}>📌</span>
              <div>
                <span style={{ fontWeight: 700, color: 'var(--navy)' }}>{pinnedACO.aco_name || pinnedACO.aco_id}</span>
                {pinnedACO.aco_state && <span style={{ color: 'var(--gray-500)', marginLeft: 6 }}>{pinnedACO.aco_state}</span>}
                <span style={{ color: 'var(--gray-400)', margin: '0 8px' }}>·</span>
                <span style={{ color: 'var(--gray-600)', fontSize: 12 }}>Pinned ACO — highlighted across all pages</span>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <button onClick={() => { setDeepDiveACO(pinnedACO); setPage('aco'); }}
                style={{ padding: '4px 12px', borderRadius: 'var(--radius)', border: '1.5px solid var(--teal)',
                  background: 'var(--teal-light)', color: 'var(--teal-dark)', cursor: 'pointer',
                  fontSize: 12, fontFamily: 'var(--font)', fontWeight: 600 }}>
                View Deep Dive →
              </button>
              <button onClick={clearPinnedACO}
                style={{ padding: '4px 10px', borderRadius: 'var(--radius)', border: '1px solid var(--gray-300)',
                  background: 'white', color: 'var(--gray-500)', cursor: 'pointer',
                  fontSize: 12, fontFamily: 'var(--font)' }}>
                ✕ Unpin
              </button>
            </div>
          </div>
        )}

        <div className="page-content">
          {loading ? (
            <div style={{ padding: '60px', textAlign: 'center', color: 'var(--gray-500)' }}>
              Loading dataset…
            </div>
          ) : (
            <>
              {page === 'overview' && <ProgramOverview perfData={perfData} navigateToACO={navigateToACO} pinnedACO={pinnedACO} />}
              {page === 'cohort'   && <CohortExplorer  perfData={perfData} navigateToACO={navigateToACO} pinnedACO={pinnedACO} pinACO={pinACO} />}
              {page === 'aco'      && <ACODeepDive      perfData={perfData} initialACO={deepDiveACO} initialVersion={deepDiveVersion} pinnedACO={pinnedACO} pinACO={pinACO} />}
              {page === 'quality'  && <QualityDashboard perfData={perfData} navigateToACO={navigateToACO} pinnedACO={pinnedACO} />}
              {page === 'changes'  && <ProgramChanges />}
              {page === 'glossary' && <Glossary navigateToACO={navigateToACO} setPage={setPage} />}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
